import { Link, useParams } from "react-router-dom";
import Layout from "@/components/site/Layout";
import Breadcrumb from "@/components/site/Breadcrumb";
import { newsArticles } from "@/mocks/articles";
import { newsCategories } from "@/mocks/categories";
import { liveFeed } from "@/mocks/live";
import { formatViews } from "@/lib/format";
import { usePageMeta } from "@/hooks/usePageMeta";

function CategoryPage() {
  const { slug = "" } = useParams();
  const category = newsCategories.find((c) => c.slug === slug);

  usePageMeta({
    title: category ? `${category.name}快讯 - 吃瓜新闻今日速递` : "栏目不存在 - 吃瓜新闻",
    description: category
      ? `吃瓜新闻「${category.name}」栏目：${category.tagline}。${category.name}相关今日要闻、即时速递与早晚报内容持续更新，快速掌握一天动态。`
      : "您访问的栏目不存在，请返回首页浏览其他快讯。",
    canonical: `https://youbijing.com.cn/category/${slug}`,
    jsonLd: category
      ? [
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "首页", item: "https://youbijing.com.cn/" },
              { "@type": "ListItem", position: 2, name: `${category.name}快讯`, item: `https://youbijing.com.cn/category/${slug}` },
            ],
          },
          {
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: `${category.name}快讯`,
            url: `https://youbijing.com.cn/category/${slug}`,
            inLanguage: "zh-CN",
          },
        ]
      : undefined,
  });

  if (!category) {
    return (
      <Layout>
        <div className="mx-auto flex max-w-6xl flex-col items-center px-4 py-24 text-center md:px-6">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-3xl text-foreground-500">
            <i className="ri-question-line" />
          </span>
          <h1 className="mt-4 font-heading text-2xl font-black text-foreground-950">没有找到这个栏目</h1>
          <p className="mt-2 text-foreground-600">该栏目可能已调整或不存在，去看看其它热点吧。</p>
          <Link
            to="/"
            className="mt-6 whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600"
          >
            返回首页
          </Link>
        </div>
      </Layout>
    );
  }

  const list = newsArticles.filter((a) => a.category === slug);
  const featured = list.find((a) => a.isTop) || list[0];
  const rest = list.filter((a) => a.id !== featured?.id);
  const others = newsCategories.filter((c) => c.slug !== slug);
  const liveTip = liveFeed.find((l) => l.scope === category.name);

  return (
    <Layout>
      {/* 栏目头 */}
      <div className="border-b border-background-200 bg-background-100">
        <div className="mx-auto max-w-6xl px-4 py-6 md:px-6 md:py-8">
          <Breadcrumb
            crumbs={[{ label: "首页", to: "/" }, { label: `${category.name}快讯` }]}
          />
          <div className="mt-4 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-accent-100 text-2xl text-accent-700">
                <i className={category.icon} />
              </span>
              <h1 className="mt-3 flex items-center gap-2 font-heading text-3xl font-black text-foreground-950 md:text-4xl">
                {category.name}
                <span className="text-lg font-semibold text-foreground-500">快讯</span>
              </h1>
              <p className="mt-2 text-sm text-foreground-600">{category.tagline} · 今日要闻与即时速递持续更新</p>
            </div>
            <div className="flex flex-wrap gap-2 text-xs">
              <span className="rounded-full bg-background-50 px-3 py-1.5 text-foreground-600">共 {list.length} 条内容</span>
              <span className="rounded-full bg-background-50 px-3 py-1.5 text-foreground-600">每日滚动更新</span>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-6xl px-4 py-8 md:px-6">
        <div className="grid gap-8 lg:grid-cols-3">
          <div className="space-y-7 lg:col-span-2">
            {/* 焦点 */}
            {featured && (
              <Link
                to={`/article/${featured.slug}`}
                className="group overflow-hidden rounded-xl border border-background-200 bg-background-50"
              >
                <img
                  src={featured.image}
                  alt={featured.title}
                  title={`${featured.title}${category.name}快讯`}
                  className="aspect-[16/8] w-full object-cover"
                />
                <div className="p-5 md:p-6">
                  <h2 className="font-heading text-xl font-bold leading-snug text-foreground-950 group-hover:text-primary-700 md:text-2xl">
                    {featured.title}
                  </h2>
                  <p className="mt-2 text-sm leading-6 text-foreground-600">{featured.summary}</p>
                  <p className="mt-3 flex flex-wrap items-center gap-3 text-xs text-foreground-500">
                    <span className="text-secondary-700">{featured.categoryName}</span>
                    <span>{featured.dateLabel}</span>
                    <span className="inline-flex items-center gap-1">
                      <i className="ri-eye-line" /> {formatViews(featured.views)}
                    </span>
                  </p>
                </div>
              </Link>
            )}

            {/* 其余列表 */}
            <div className="space-y-4">
              <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
                <i className="ri-article-line text-primary-500" /> 更多{category.name}内容
              </h2>
              {rest.length > 0 ? (
                rest.map((a, i) => (
                  <Link
                    key={a.id}
                    to={`/article/${a.slug}`}
                    className="group flex gap-4 rounded-xl border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300 hover:bg-background-100"
                  >
                    <span className="mt-1 font-heading text-3xl font-black text-background-200 transition-colors group-hover:text-primary-300">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <div className="min-w-0 flex-1">
                      <h3 className="font-heading text-lg font-bold leading-snug text-foreground-900 group-hover:text-primary-700">
                        {a.title}
                      </h3>
                      <p className="mt-1.5 line-clamp-2 text-sm leading-6 text-foreground-500">{a.summary}</p>
                      <p className="mt-2.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-foreground-500">
                        <span>{a.author}</span>
                        <span>{a.dateLabel} {a.timeLabel}</span>
                        <span className="inline-flex items-center gap-1">
                          <i className="ri-eye-line" /> {formatViews(a.views)}
                        </span>
                      </p>
                    </div>
                    <img
                      src={a.image}
                      alt={a.title}
                      title={`${a.title}${category.name}快讯推荐`}
                      className="hidden h-28 w-40 shrink-0 rounded-lg object-cover sm:block"
                    />
                  </Link>
                ))
              ) : (
                <p className="rounded-xl border border-dashed border-background-200 py-12 text-center text-sm text-foreground-500">
                  该栏目内容整理中，请稍后再来。
                </p>
              )}
            </div>
          </div>

          {/* 右栏 */}
          <aside className="space-y-8" aria-label="相关栏目">
            <section className="rounded-xl border border-background-200 bg-background-50 p-5">
              <h3 className="font-heading text-lg font-black text-foreground-950">其它栏目</h3>
              <ul className="mt-4 space-y-1">
                {others.map((c) => (
                  <li key={c.slug}>
                    <Link
                      to={`/category/${c.slug}`}
                      className="group flex items-center justify-between rounded-lg px-3 py-2.5 transition-colors hover:bg-background-100"
                    >
                      <span className="flex items-center gap-2.5 text-sm text-foreground-700 group-hover:text-primary-700">
                        <i className={`${c.icon} text-primary-500`} />
                        {c.name}快讯
                      </span>
                      <i className="ri-arrow-right-s-line text-foreground-300" />
                    </Link>
                  </li>
                ))}
              </ul>
            </section>

            {liveTip && (
              <section className="rounded-xl bg-primary-600 p-5 text-background-50">
                <h3 className="flex items-center gap-2 font-heading text-lg font-black">
                  <span className="h-2 w-2 rounded-full bg-accent-400 animate-pulse-dot" />
                  正在直播
                </h3>
                <p className="mt-3 text-sm leading-6 text-background-50/85">{liveTip.text}</p>
                <Link
                  to="/live"
                  className="mt-4 inline-flex items-center gap-1.5 rounded-md bg-background-50 px-4 py-2 text-sm font-medium text-primary-700 transition-colors hover:bg-accent-100"
                >
                  <i className="ri-play-circle-line" /> 进入直播频道
                </Link>
              </section>
            )}

            <Link
              to="/about"
              className="flex items-center justify-between rounded-xl border border-background-200 bg-background-50 p-5 transition-colors hover:border-primary-300"
            >
              <span>
                <span className="block font-heading text-base font-bold text-foreground-900">了解吃瓜新闻</span>
                <span className="mt-1 block text-sm text-foreground-500">关于我们的定位与内容原则</span>
              </span>
              <i className="ri-arrow-right-line text-primary-500" />
            </Link>
          </aside>
        </div>
      </div>
    </Layout>
  );
}

export default CategoryPage;